import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Clock, Users, MapPin, ChevronRight, Award, CheckCircle, Download, FileText, Calendar, Flame } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ProgrammeDownloadDialog } from '../components/programme/ProgrammeDownloadDialog';
import { ProgramTimeline } from '../components/programme/ProgramTimeline';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { QualiopiInfoSection } from '../components/QualiopiInfoSection';

const categoryLabels = {
  decouverte: 'Découverte',
  complete: 'Formation complète',
  auto: 'Sellerie automobile',
  moto: 'Sellerie moto',
  nautique: 'Sellerie nautique',
  habitat: 'Habitat & médical',
  dessin: 'Dessin technique'
};

const categoryImages = {
  decouverte: 'https://images.unsplash.com/photo-1556195332-95503f664ced?w=1600&q=80&auto=format&fit=crop',
  complete: 'https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=1600&q=80&auto=format&fit=crop',
  auto: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80&auto=format&fit=crop',
  moto: 'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=1600&q=80&auto=format&fit=crop',
  nautique: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1600&q=80&auto=format&fit=crop',
  habitat: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=1600&q=80&auto=format&fit=crop',
  dessin: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=1600&q=80&auto=format&fit=crop'
};

export const CourseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProgrammeDialog, setShowProgrammeDialog] = useState(false);

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const c = await api.getCourse(id);
      setCourse(c);
      if (c.course_type === 'onsite') {
        const s = await api.getSessions(id);
        setSessions(s);
      }
    } catch (e) {
      console.error(e);
      toast.error('Formation non trouvée');
      navigate('/formations-sur-place');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] pt-40 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C9B896] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!course) return null;

  const futureSessions = sessions
    .filter(s => parseISO(s.start_date) > new Date())
    .sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date));
  const firstSession = futureSessions[0];
  const spotsLeft = firstSession ? firstSession.available_spots - firstSession.enrolled_count : 0;

  const heroImg = course.thumbnail_url || categoryImages[course.category] || categoryImages.complete;
  const categoryLong = categoryLabels[course.category] || course.category;

  return (
    <div className="bg-[#FAF7F0] text-[#1F2A4A]">
      {/* HERO ─ compact split layout */}
      <section className="pt-24 md:pt-28 pb-6 md:pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-4">
            <Link to="/" className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <Link to={L('/formations-sur-place')} className="hover:text-[#C9B896] transition-colors">Formations</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <span className="text-[#1F2A4A] truncate">{course.title}</span>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:gap-8 items-center">
            {/* Image */}
            <div className="col-span-12 lg:col-span-6">
              <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#1F2A4A]">
                <img src={heroImg} alt={course.title} className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Intro */}
            <div className="col-span-12 lg:col-span-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#4A5568]/85 mb-2">{categoryLong}</p>
              <h1 className="font-light text-[#1F2A4A] leading-[1.1] text-2xl md:text-3xl lg:text-4xl mb-3" data-testid="course-title">
                {course.title}
              </h1>
              <p className="text-[#4A5568] font-light text-sm leading-relaxed mb-4 line-clamp-3">
                {course.description}
              </p>

              {/* Meta pills */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="border border-dashed border-[#1F2A4A]/15 rounded-xl px-3 py-2">
                  <Clock className="w-3.5 h-3.5 text-[#C9B896] mb-1" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-semibold">Durée</p>
                  <p className="text-sm font-bold text-[#1F2A4A]">{course.duree_detail || `${course.duration_days} jours`}</p>
                </div>
                <div className="border border-amber-400/40 rounded-xl px-3 py-2 bg-amber-50/60">
                  <Users className="w-3.5 h-3.5 text-amber-700 mb-1" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Places</p>
                  <p className="text-sm font-bold text-amber-900">{firstSession ? `${course.max_participants || 3} max · vite\u00a0!` : `${course.max_participants || 3} stagiaires max`}</p>
                </div>
                <div className="border border-dashed border-[#1F2A4A]/15 rounded-xl px-3 py-2">
                  <MapPin className="w-3.5 h-3.5 text-[#C9B896] mb-1" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-semibold">Lieu</p>
                  <p className="text-sm font-bold text-[#1F2A4A]">Istres</p>
                </div>
              </div>

              {/* Next-session highlight — visible at the top of the hero so a
                  visitor instantly knows when the next cohort starts and how
                  many seats are left. */}
              {course.course_type === 'onsite' && firstSession && (
                <div
                  className="rounded-xl border border-[#1F2A4A]/12 bg-white px-4 py-3 mb-4 flex flex-wrap items-center gap-x-5 gap-y-2"
                  data-testid="course-next-session"
                >
                  <div className="flex items-center gap-2.5">
                    <Calendar className="w-4 h-4 text-[#C9B896]" strokeWidth={2} />
                    <div className="leading-tight">
                      <p className="text-[9px] uppercase tracking-[0.22em] text-[#4A5568]">Prochaine session</p>
                      <p className="text-sm font-medium text-[#1F2A4A]">
                        Du {format(parseISO(firstSession.start_date), 'd MMM', { locale: fr })} au {format(parseISO(firstSession.end_date), 'd MMM yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                  <div className="h-7 w-px bg-[#1F2A4A]/10 hidden sm:block" />
                  <div className="flex items-center gap-2 leading-tight">
                    {spotsLeft > 0 ? (
                      <>
                        <Flame className={`w-3.5 h-3.5 ${spotsLeft === 1 ? 'text-red-600' : 'text-amber-600'}`} strokeWidth={2} />
                        <span className={`text-sm font-semibold ${spotsLeft === 1 ? 'text-red-700' : 'text-amber-800'}`}>
                          {spotsLeft === 1 ? 'Dernière place — vite\u00a0!' : `${spotsLeft} place${spotsLeft > 1 ? 's' : ''} restante${spotsLeft > 1 ? 's' : ''}`}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm font-semibold text-red-700">Complet — voir autres dates</span>
                    )}
                  </div>
                  {futureSessions.length > 1 && (
                    <a
                      href="#sessions-list"
                      className="ml-auto inline-flex items-center gap-1 text-xs text-[#1F2A4A]/85 hover:text-[#C9B896] border-b border-[#C9B896]/60 hover:border-[#C9B896] pb-0.5 transition-colors"
                      data-testid="see-other-dates-link"
                    >
                      Voir les {futureSessions.length - 1} autre{futureSessions.length > 2 ? 's' : ''} date{futureSessions.length > 2 ? 's' : ''}
                      <ChevronRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}

              {/* Price + CTA */}
              <div className="flex items-end justify-between pb-3 mb-3 border-b border-dashed border-[#1F2A4A]/15">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A5568]">Tarif formation</p>
                  <p className="text-3xl font-bold text-[#1F2A4A] leading-tight">
                    {course.price.toFixed(0)}<span className="text-xl text-[#8a7340] ml-1 font-semibold">€ TTC</span>
                  </p>
                  <p className="text-[10px] text-[#4A5568]/70">Plusieurs financements possibles</p>
                </div>
              </div>

              {firstSession && spotsLeft > 0 ? (
                <Link to={L(`/candidature/${firstSession.id}`)} className="block w-full">
                  <Button className="w-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full py-5 text-xs uppercase tracking-[0.25em] font-medium shadow-xl shadow-[#1F2A4A]/15" data-testid="course-apply-cta">
                    Candidater
                  </Button>
                </Link>
              ) : (
                <Link
                  to={L(`/contact?subject=${encodeURIComponent('Demande de session — ' + course.title)}&message=${encodeURIComponent('Bonjour,\n\nJe suis intéressé(e) par la formation « ' + course.title + ' ». Pourriez-vous me communiquer les prochaines dates de session disponibles ?\n\nCordialement,')}`)}
                  className="block w-full"
                  data-testid="request-session-link"
                >
                  <Button variant="outline" className="w-full border-[#1F2A4A]/20 text-[#1F2A4A] hover:bg-[#1F2A4A] hover:text-[#FAF7F0] rounded-full py-5 text-xs uppercase tracking-[0.25em] font-medium">
                    Demander une session
                  </Button>
                </Link>
              )}

              {course.programme_pdf_path && (
                <button
                  type="button"
                  onClick={() => setShowProgrammeDialog(true)}
                  className="mt-2 w-full flex items-center justify-center gap-2 text-[#1F2A4A] border border-[#1F2A4A]/20 hover:border-[#C9B896] hover:bg-[#C9B896]/10 rounded-full py-2.5 text-[10px] uppercase tracking-[0.25em] font-medium transition-colors"
                  data-testid="programme-download-cta"
                >
                  <Download className="w-3.5 h-3.5 text-[#C9B896]" />
                  Télécharger le programme
                </button>
              )}

              {/* Welcome-gift highlight — small but eye-catching badge so
                  prospects see the perk before they scroll. */}
              <div
                className="mt-4 flex items-center gap-3 rounded-xl bg-gradient-to-br from-[#C9B896]/15 via-[#FAF7F0] to-[#C9B896]/10 border border-[#C9B896]/40 px-3.5 py-3"
                data-testid="welcome-gift-highlight"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-[#1F2A4A] text-[#C9B896] flex items-center justify-center text-lg" aria-hidden>
                  🎁
                </div>
                <div className="leading-tight">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9B896] font-semibold">
                    Inclus dans la formation
                  </p>
                  <p className="text-sm font-medium text-[#1F2A4A] mt-0.5">
                    Un cadeau de bienvenue <span className="text-[#4A5568] font-light italic">offert à chaque stagiaire</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stitched separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* SECTION 02 — Programme détaillé (PDF preview + gated download) */}
      {course.programme_pdf_path && (
        <section className="py-12 md:py-16 bg-[#F4EFE3]/40" data-testid="programme-detail-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
              {/* Preview thumbnail */}
              <div className="md:col-span-5 lg:col-span-5 min-w-0 w-full max-w-md md:max-w-none mx-auto md:mx-0">
                <div className="relative">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-white shadow-2xl shadow-[#1F2A4A]/15 border border-[#1F2A4A]/10">
                    <img
                      src={`/programmes-preview/${course.id}-1.png`}
                      alt={`Aperçu du programme — ${course.title}`}
                      className="block w-full h-full object-cover object-top"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      data-testid="programme-preview-image"
                    />
                  </div>
                  {/* Fade-out at bottom hinting "gated" */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 rounded-b-xl bg-gradient-to-t from-[#F4EFE3] via-[#F4EFE3]/60 to-transparent" />
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1F2A4A] text-[#FAF7F0] text-[10px] uppercase tracking-[0.25em] shadow-lg whitespace-nowrap max-w-[92%]">
                    <FileText className="w-3.5 h-3.5 text-[#C9B896] shrink-0" />
                    <span className="truncate">PDF · Page 1 sur le programme complet</span>
                  </div>
                </div>
              </div>

              {/* CTA & arguments */}
              <div className="md:col-span-7 lg:col-span-7 min-w-0">
                <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A] mb-6">
                  Téléchargez le <span className="font-serif italic">programme complet.</span>
                </h2>
                <p className="text-[#4A5568] text-lg font-light leading-relaxed mb-7">
                  Objectifs, contenu jour par jour, méthode pédagogique, modalités d'évaluation, débouchés et tarifs détaillés.
                  Le programme officiel <strong className="text-[#1F2A4A] font-medium">conforme Qualiopi</strong> envoyé en un clic, après avoir renseigné vos coordonnées.
                </p>
                <ul className="space-y-3 mb-9">
                  {[
                    'Document PDF officiel — versions à jour 2025-2026',
                    'Détail journée par journée des compétences acquises',
                    'Récapitulatif des dispositifs de financement éligibles',
                  ].map((line, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#1F2A4A]/90">
                      <CheckCircle className="w-4 h-4 text-[#C9B896] mt-0.5 shrink-0" strokeWidth={2} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setShowProgrammeDialog(true)}
                  className="group inline-flex items-center gap-3 bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-8 py-4 text-sm uppercase tracking-[0.25em] font-medium shadow-xl shadow-[#1F2A4A]/15 transition-all hover:gap-5"
                  data-testid="programme-section-cta"
                >
                  <Download className="w-4 h-4 text-[#C9B896]" />
                  Télécharger le programme PDF
                  <span className="text-[#C9B896] transition-transform group-hover:translate-x-1">→</span>
                </button>
                <p className="text-[11px] text-[#4A5568]/70 mt-4">
                  Téléchargement immédiat. Conforme RGPD — vous gardez la maîtrise de vos données.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 02bis — Qualiopi mandatory info (moved up so Prérequis,
          Public, Délai d'accès… are reached without scrolling through the
          full day-by-day timeline). */}
      <QualiopiInfoSection info={course.qualiopi_info} />

      {/* SECTION — Objectifs (blocs de compétences) */}
      {course.objectives_blocks?.length > 0 && (
        <section className="py-12 md:py-16" data-testid="objectives-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-2 font-semibold">Objectifs</p>
            <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A] mb-8">
              Ce que vous saurez <span className="font-serif italic">faire.</span>
            </h2>
            <div className={`grid gap-6 ${course.objectives_blocks.length > 1 ? 'md:grid-cols-3' : 'md:grid-cols-1 max-w-3xl'}`}>
              {course.objectives_blocks.map((b, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#1F2A4A]/10 p-7">
                  <h3 className="text-[#1F2A4A] font-medium mb-4 leading-snug">{b.title}</h3>
                  <ul className="space-y-2.5">
                    {b.items.map((it, j) => (
                      <li key={j} className="flex gap-2.5 items-start text-sm text-[#4A5568]">
                        <CheckCircle className="w-4 h-4 text-[#C9B896] shrink-0 mt-0.5" strokeWidth={2} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION — Taux de réussite / satisfaction */}
      {course.taux && (
        <section className="py-12 md:py-14 bg-[#1F2A4A] text-white" data-testid="taux-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
            <div className="text-center md:text-left">
              <p className="font-serif italic text-5xl text-[#C9B896] mb-1">100%</p>
              <p className="text-sm text-[#E8DCC8]/85">{course.taux.reussite_global}</p>
              {(course.taux.detail || []).map((d, i) => <p key={i} className="text-xs text-[#E8DCC8]/60 mt-1">{d}</p>)}
            </div>
            <div className="text-center md:text-left">
              <p className="font-serif italic text-5xl text-[#C9B896] mb-1">92%</p>
              <p className="text-sm text-[#E8DCC8]/85">{course.taux.satisfaction}</p>
            </div>
            <div className="flex items-center justify-center md:justify-end">
              <p className="text-xs text-[#E8DCC8]/50 italic">{course.taux.maj}</p>
            </div>
          </div>
        </section>
      )}

      {/* SECTION — Méthodes pédagogiques + Évaluation */}
      {(course.methodes?.length > 0 || course.evaluation) && (
        <section className="py-12 md:py-16" data-testid="methodes-evaluation-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10">
            {course.methodes?.length > 0 && (
              <div>
                <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-6">Méthodes <span className="font-serif italic">pédagogiques.</span></h2>
                <ul className="space-y-3">
                  {course.methodes.map((m, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm text-[#4A5568]">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-[#1F2A4A] text-[#C9B896] flex items-center justify-center text-[11px] font-serif italic mt-0.5">{i + 1}</span>
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {course.evaluation && (
              <div>
                <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-6">Évaluation <span className="font-serif italic">& certification.</span></h2>
                <div className="space-y-4 text-sm text-[#4A5568] leading-relaxed">
                  {course.evaluation.pendant && <p><strong className="text-[#1F2A4A]">Pendant :</strong> {course.evaluation.pendant}</p>}
                  {course.evaluation.apres && <p><strong className="text-[#1F2A4A]">Après :</strong> {course.evaluation.apres}</p>}
                  {course.evaluation.important && (
                    <p className="border-l-2 border-[#C9B896] pl-4 italic">{course.evaluation.important}</p>
                  )}
                  {course.certification_link && (
                    <Link to={L(course.certification_link)} className="inline-block text-[#1F2A4A] underline underline-offset-4 decoration-[#C9B896] hover:text-[#C9B896] transition-colors" data-testid="certification-page-link">
                      En savoir plus sur la certification →
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* SECTION — Modules à la carte */}
      {course.modules_catalogue?.length > 0 && (
        <section className="py-12 md:py-16 bg-[#F4EFE3]/60" data-testid="modules-carte-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-2 font-semibold">À la carte</p>
            <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A] mb-3">
              Modules <span className="font-serif italic">à la carte.</span>
            </h2>
            <p className="text-[#4A5568] text-sm font-light mb-8 max-w-2xl">
              Besoin d'une compétence précise ? Chaque module peut se suivre indépendamment.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {course.modules_catalogue.map((m, i) => (
                <div key={i} className="bg-white rounded-xl border border-[#1F2A4A]/10 p-5 flex flex-col hover:shadow-md transition-shadow" data-testid={`module-card-${i}`}>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8a7340] font-bold mb-1.5">Module {String(i + 1).padStart(2, '0')} · {m.hours} h / {m.days} j</p>
                  <h3 className="text-[#1F2A4A] font-medium text-[15px] leading-snug mb-2">{m.title}</h3>
                  <p className="text-xs text-[#4A5568] leading-relaxed flex-1">{m.description}</p>
                  <p className="mt-3 pt-3 border-t border-dashed border-[#1F2A4A]/10 text-[#1F2A4A] font-bold text-xl">{m.price}<span className="text-sm text-[#8a7340] font-semibold"> € TTC</span></p>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to={L(`/contact?subject=${encodeURIComponent('Module à la carte — ' + course.title)}`)} className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500">
                <span className="text-sm uppercase tracking-[0.25em]">Réserver un module</span>
                <span className="text-[#C9B896]">→</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 03 — Programme jour par jour (collapsible, closed by default
          to keep the scroll path short). */}
      {course.program_days?.length > 0 && (
        <section className="py-12 md:py-16" data-testid="program-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <details className="group rounded-2xl border border-[#1F2A4A]/12 bg-white open:bg-[#FAF7F0]">
              <summary
                className="list-none cursor-pointer flex flex-wrap items-end gap-x-6 gap-y-3 px-6 md:px-10 py-6 md:py-8"
                data-testid="program-toggle"
              >
                <div className="flex-1 min-w-[60%]">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-2 font-semibold">
                    Proposition de programme
                  </p>
                  <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A]">
                    Le contenu, <span className="font-serif italic">jour par jour.</span>
                  </h2>
                  <p className="text-[#4A5568] mt-3 text-sm md:text-base font-light leading-relaxed">
                    {course.duration_days || course.program_days.length} journée{(course.duration_days || course.program_days.length) > 1 ? 's' : ''} de formation détaillée{(course.duration_days || course.program_days.length) > 1 ? 's' : ''} — clique pour voir le programme complet.
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1F2A4A] border border-[#1F2A4A]/15 rounded-full px-4 py-2 group-open:bg-[#1F2A4A] group-open:text-[#FAF7F0] transition-colors">
                  <span className="group-open:hidden">Afficher le programme</span>
                  <span className="hidden group-open:inline">Masquer le programme</span>
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                </span>
              </summary>

              <div className="px-6 md:px-10 pb-10 pt-2 md:max-w-4xl">
                <p className="text-xs text-[#1F2A4A]/55 italic mb-6">
                  Le programme pourra être adapté à tes besoins, ton expérience et tes motivations, sans déroger aux étapes essentielles.
                </p>
                <ProgramTimeline days={course.program_days} />
              </div>
            </details>
          </div>
        </section>
      )}

      {/* SECTION 03bis — Modules (online courses fallback, also collapsible) */}
      {course.modules?.length > 0 && !course.program_days?.length && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <details className="group rounded-2xl border border-[#1F2A4A]/12 bg-white open:bg-[#FAF7F0]">
              <summary className="list-none cursor-pointer flex flex-wrap items-end gap-x-6 gap-y-3 px-6 md:px-10 py-6 md:py-8" data-testid="modules-toggle">
                <div className="flex-1 min-w-[60%]">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-2 font-semibold">
                    Proposition de programme
                  </p>
                  <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A]">
                    Le contenu, <span className="font-serif italic">jour par jour.</span>
                  </h2>
                  <p className="text-[#4A5568] mt-3 text-sm md:text-base font-light leading-relaxed">
                    {course.modules.length} module{course.modules.length > 1 ? 's' : ''} — clique pour voir le détail.
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1F2A4A] border border-[#1F2A4A]/15 rounded-full px-4 py-2 group-open:bg-[#1F2A4A] group-open:text-[#FAF7F0] transition-colors">
                  <span className="group-open:hidden">Afficher le programme</span>
                  <span className="hidden group-open:inline">Masquer le programme</span>
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                </span>
              </summary>

              <ul className="border-t border-[#1F2A4A]/12 px-6 md:px-10 pb-6">
                {course.modules.map((m, idx) => (
                  <li
                    key={m.id || idx}
                    className="border-b border-dashed border-[#1F2A4A]/15 py-6 md:py-7 grid grid-cols-12 gap-4 items-baseline"
                    data-testid={`module-${idx}`}
                  >
                    <h3 className="col-span-10 md:col-span-7 text-xl font-light text-[#1F2A4A]">{m.title}</h3>
                    <p className="col-span-12 md:col-span-4 text-sm text-[#4A5568] font-light leading-relaxed">{m.description}</p>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </section>
      )}

      {/* SECTION 03 — Sessions disponibles (onsite only) */}
      {course.course_type === 'onsite' && (
        <section id="sessions-list" className="py-12 md:py-16 bg-[#F4EFE3]" data-testid="course-sessions-list">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A] mb-6 md:mb-10">
              Prochaines <span className="font-serif italic">ouvertures.</span>
            </h2>

            {futureSessions.length === 0 ? (
              <div className="border border-dashed border-[#1F2A4A]/20 rounded-xl p-12 text-center">
                <p className="text-[#4A5568] font-light text-lg mb-6">Aucune session planifiée pour le moment.</p>
                <Link to={L('/contact')} className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500">
                  <span className="text-sm uppercase tracking-[0.25em]">Être notifié·e</span>
                  <span className="text-[#C9B896]">→</span>
                </Link>
              </div>
            ) : (
              <ul className="border-t border-[#1F2A4A]/12">
                {futureSessions.map((s, idx) => {
                  const left = s.available_spots - s.enrolled_count;
                  return (
                    <li
                      key={s.id}
                      className="border-b border-dashed border-[#1F2A4A]/15 py-6 grid grid-cols-12 gap-4 items-center hover:bg-white/60 transition-colors"
                      data-testid={`session-row-${idx}`}
                    >
                      <div className="col-span-10 md:col-span-4">
                        <p className="text-[10px] uppercase tracking-wider text-[#C9B896]/80 mb-1">Du</p>
                        <p className="text-lg font-light text-[#1F2A4A]">
                          {format(parseISO(s.start_date), 'd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <div className="hidden md:block col-span-3">
                        <p className="text-[10px] uppercase tracking-wider text-[#C9B896]/80 mb-1">Au</p>
                        <p className="text-base text-[#4A5568] font-light">
                          {format(parseISO(s.end_date), 'd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <div className="col-span-7 md:col-span-2">
                        <span className={`text-sm font-medium ${left > 3 ? 'text-green-700' : left > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                          {left > 0 ? `${left} places` : 'Complet'}
                        </span>
                      </div>
                      <div className="col-span-5 md:col-span-2 flex justify-end">
                        {left > 0 ? (
                          <Link to={L(`/candidature/${s.id}`)} className="group inline-flex items-baseline gap-2 text-[#1F2A4A] border-b border-[#C9B896] pb-0.5 hover:gap-4 transition-all duration-500">
                            <span className="text-xs uppercase tracking-[0.2em]">Candidater</span>
                            <span className="text-[#C9B896]">→</span>
                          </Link>
                        ) : (
                          <span className="text-xs text-[#4A5568]/55">—</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* SECTION 04 — Final CTA (Qualiopi pill removed at client request —
          the official badge stays in the footer only). */}
      <section className="py-12 md:py-16 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A] mb-8">
            Une question avant <span className="font-serif italic">de candidater&nbsp;?</span>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to={L('/contact')}>
              <Button variant="outline" className="border-[#1F2A4A]/20 text-[#1F2A4A] hover:bg-[#1F2A4A] hover:text-[#FAF7F0] rounded-full px-9 py-7 text-sm uppercase tracking-[0.25em] font-medium">
                Nous contacter
              </Button>
            </Link>
            {firstSession && spotsLeft > 0 && (
              <Link to={L(`/candidature/${firstSession.id}`)} className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500">
                <span className="text-sm uppercase tracking-[0.25em]">Déposer ma candidature</span>
                <span className="text-[#C9B896]">→</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      <ProgrammeDownloadDialog
        open={showProgrammeDialog}
        onOpenChange={setShowProgrammeDialog}
        course={course}
      />
    </div>
  );
};
